import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const capabilities = [
  { title: 'Supplier Identification', description: 'Comprehensive market research and supplier shortlisting based on your exact product specifications, quality requirements, and budget.', icon: 'ri-search-eye-line' },
  { title: 'Factory Audits & Verification', description: 'On-site factory inspections, compliance audits, financial stability checks, and production capability assessments before any commitment.', icon: 'ri-building-2-line' },
  { title: 'Quality Control', description: 'Multi-stage quality inspections — raw material check, in-process inspection, pre-shipment verification, and full compliance documentation.', icon: 'ri-shield-check-line' },
  { title: 'Price Negotiation', description: 'Expert commercial negotiation ensuring optimal pricing, favorable payment terms, MOQ flexibility, and robust contractual protection.', icon: 'ri-money-dollar-circle-line' },
  { title: 'Sample Management', description: 'Professional sample procurement, inspection, testing, and delivery management so you can evaluate products before committing to bulk orders.', icon: 'ri-box-3-line' },
  { title: 'Contract Management', description: 'Comprehensive contract drafting, review, and enforcement — protecting your interests across jurisdictions with clear terms and conditions.', icon: 'ri-file-text-line' },
];

const advantageItems = [
  { number: '500+', label: 'Verified Suppliers' },
  { number: '98%', label: 'Order Accuracy Rate' },
  { number: '30%', label: 'Average Cost Savings' },
  { number: '100%', label: 'Quality Guarantee' },
];

export default function ProcurementPage() {
  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-[80px]">

        {/* Hero */}
        <section className="relative w-full bg-primary-900 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20procurement%20and%20sourcing%20office%20with%20product%20samples%20on%20desk%2C%20quality%20inspection%20documents%2C%20professionals%20reviewing%20supplier%20contracts%2C%20clean%20corporate%20environment%2C%20warm%20natural%20lighting%2C%20premium%20business%20photography&width=1920&height=800&seq=procurement-hero&orientation=landscape"
              alt=""
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary-950/90 via-primary-950/80 to-primary-950/95"></div>
          </div>
          <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 py-16 md:py-24">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-400 mb-4">Services</span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-5 leading-tight">
              Procurement
            </h1>
            <p className="text-white/50 max-w-2xl text-sm md:text-base leading-relaxed mb-8">
              End-to-end procurement solutions from supplier identification and verification through to quality control and delivery — ensuring you get exactly what you need, at the right price, from the right source.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="px-7 py-3.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-accent-500/25">
                Start Sourcing
              </Link>
              <Link to="/products" className="px-7 py-3.5 bg-white/10 backdrop-blur-sm text-white font-semibold text-sm rounded-full hover:bg-white/20 border border-white/30 transition-all duration-300 whitespace-nowrap cursor-pointer">
                View Catalogue
              </Link>
            </div>
          </div>
        </section>

        {/* Overview */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">Overview</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">Strategic Procurement, Simplified</h2>
              <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                Sourcing products from China can be a minefield of quality issues, communication barriers, hidden costs, and unreliable suppliers. BEKAALI&apos;s procurement division eliminates these risks with a proven, structured approach backed by our on-the-ground presence in China.
              </p>
            </div>

            <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
              <p>
                Our procurement service is built around a simple principle: <strong>we take full accountability for results.</strong> Unlike traditional sourcing agents who simply connect you with suppliers and step back, we manage every stage of the procurement lifecycle — from initial market research and supplier qualification through to production monitoring, quality control, shipping, and delivery.
              </p>
              <p>
                What sets BEKAALI&apos;s procurement apart is our rigorous supplier verification process. Every manufacturer in our network has been physically audited by our China-based team. We verify factory capabilities, financial stability, quality management systems, compliance certifications, and production capacity before adding any supplier to our approved list. This isn&apos;t a paper exercise — our team visits factories, inspects production lines, and builds relationships with management.
              </p>
              <p>
                The result? Our clients consistently report 30%+ cost savings compared to sourcing independently, with significantly lower defect rates and faster delivery times. We handle the complexity so you can focus on growing your business.
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {advantageItems.map((stat, i) => (
              <div key={i} className="text-center p-6 bg-background-50 border border-background-200/70 rounded-2xl">
                <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-1">{stat.number}</div>
                <div className="text-xs text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Capabilities */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-24 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-600 mb-4">Capabilities</span>
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-4">Procurement Services</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {capabilities.map((cap, i) => (
                <div key={i} className="group bg-background-100 border border-background-200/70 rounded-2xl p-6 md:p-7 hover:border-accent-200/50 transition-all duration-400">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-secondary-200 text-secondary-600 mb-4 group-hover:bg-secondary-500 group-hover:text-white transition-all duration-400">
                    <i className={`${cap.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">{cap.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{cap.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">Let&apos;s Source Smarter</h2>
            <p className="text-foreground-600 max-w-xl mx-auto text-sm md:text-base leading-relaxed mb-8">
              Tell us what you need — we&apos;ll find the right supplier, negotiate the best price, and manage quality through to delivery.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 text-primary-900 font-semibold text-sm rounded-full hover:bg-accent-400 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-accent-500/25">
              Start Your Procurement <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}